
import { PlanType } from '@/utils/types';
import { OnboardingFormData, SignupData } from '@/types/onboarding.d';
import { SaasInstance } from '@/types/userRole';

export const getAllInstances = async (): Promise<SaasInstance[]> => {
  // In a real app, this would fetch from an API
  // For now, returning an empty array since we're removing mock data
  return [];
};

export const getInstanceById = async (id: string): Promise<SaasInstance | undefined> => {
  // In a real app, this would fetch a specific instance from an API
  // For now, returning undefined since we're removing mock data
  return undefined;
};

export const searchInstances = async (query: string): Promise<SaasInstance[]> => {
  // In a real app, this would search instances via an API
  // For now, returning an empty array since we're removing mock data
  return [];
};

export const getInstanceStats = async () => {
  // In a real app, this would fetch stats from an API
  return {
    total: 0,
    active: 0,
    trial: 0,
    enterprise: 0
  };
};

// Generate a random system subdomain with 'saas' prefix followed by 6 random digits
export const generateSystemSubdomain = (): string => {
  const randomNumber = Math.floor(100000 + Math.random() * 900000); // 6-digit number
  return `saas${randomNumber}`;
};

export const createInstance = async (instanceData: {
  name: string;
  domain: string;
  plan: PlanType;
  primaryColor?: string;
  resellerSubdomain?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}): Promise<SaasInstance> => {
  // In a real app, this would create an instance via an API
  const newInstance: SaasInstance = {
    id: `instance-${Date.now()}`,
    name: instanceData.name,
    domain: instanceData.domain,
    subdomain: instanceData.domain.split('.')[0],
    status: 'active',
    plan: instanceData.plan,
    ownerId: 'owner-id',
    createdAt: new Date().toISOString(), // Convert Date to string
    customization: {
      primaryColor: instanceData.primaryColor || '#4A80F0',
      logo: '/logo.png'
    },
    settings: {
      logoUrl: '/logo.png',
      primaryColor: instanceData.primaryColor || '#4A80F0'
    }
  };
  
  return newInstance;
};

// Alias for createInstance to match existing imports
export const createNewInstance = createInstance;

// Add function to check subdomain availability
export const checkSubdomainAvailability = async (subdomain: string): Promise<boolean> => {
  // In a real implementation, this would check against an API
  // For now, simulate all subdomains are available since we're removing mock data
  
  // Simulating some taken subdomains for testing
  const takenSubdomains = ['admin', 'test', 'demo', 'api', 'app', 'auth', 'main', 'billing'];
  
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return !takenSubdomains.includes(subdomain.toLowerCase());
};

// Function for creating instances from onboarding
export const createInstanceFromOnboarding = async (
  signupData: SignupData,
  onboardingData: OnboardingFormData
): Promise<{ success: boolean; instanceId?: string; message?: string }> => {
  try {
    // Convert signup plan to PlanType
    const planType = convertSignupPlanToPlanType(signupData.plan);
    
    // Generate a system subdomain for the instance
    const systemSubdomain = generateSystemSubdomain();
    
    // In a real app, this would create an instance via an API
    const resellerDomain = onboardingData.resellerSubdomain 
      ? `${systemSubdomain}.${onboardingData.resellerSubdomain}.referme.site` 
      : `${systemSubdomain}.referme.site`;
    
    const newInstance = await createInstance({
      name: onboardingData.companyName,
      domain: resellerDomain,
      plan: planType,
      primaryColor: onboardingData.primaryColor,
      resellerSubdomain: onboardingData.resellerSubdomain,
      stripeCustomerId: onboardingData.stripeCustomerId,
      stripeSubscriptionId: onboardingData.stripeSubscriptionId
    });
    
    // In a real app, this would also send a welcome email with login credentials
    console.log(`Welcome email would be sent to ${signupData.email} with login credentials for ${resellerDomain}`);
    
    return {
      success: true,
      instanceId: newInstance.id
    };
  } catch (error) {
    console.error('Error creating instance from onboarding:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to create instance'
    };
  }
};

// Helper function to convert signup plan to PlanType
function convertSignupPlanToPlanType(signupPlan: string): PlanType {
  switch (signupPlan) {
    case 'starter':
      return 'basic';
    case 'growth':
      return 'premium';
    case 'expert':
    case 'enterprise':
      return 'enterprise';
    default:
      return 'free';
  }
}

// These functions exist to maintain compatibility with existing code
export const generateMockInstances = async (): Promise<SaasInstance[]> => {
  // In a real app, this would fetch from an API
  // For now, returning an empty array since we're removing mock data
  return [];
};

export const filterInstances = (instances: SaasInstance[], query: string): SaasInstance[] => {
  if (!query) return instances;
  
  const lowerCaseQuery = query.toLowerCase();
  return instances.filter(instance => 
    instance.name.toLowerCase().includes(lowerCaseQuery) || 
    (instance.domain && instance.domain.toLowerCase().includes(lowerCaseQuery))
  );
};
